/**
 * Canonical shape vocabulary — THE single source of truth.
 *
 * Consumed by:
 *  1. `heera-mapping.ts::mapShape` — every read-time / feed-time shape
 *     normalization must land on one of these labels (unit-enforced in
 *     heera-mapping.test.ts).
 *  2. The web filter UI — `web/src/lib/filterHelpers.ts` re-exports
 *     these arrays (relative import across packages; this file must stay
 *     dependency-free and browser-safe: no Node imports, no side effects).
 *  3. The admin shape-override endpoint (`POST /admin/stones/:id/shape-override`)
 *     — only canonical labels are accepted as overrides.
 *
 * Rename rule: a label here is buyer-visible (filter chips + facets). If
 * you rename one, mapShape's output changes with it — grep for the old
 * label across web/ before renaming.
 */

/** The high-signal shapes, always shown first (original Heera 10). */
export const SHAPES_CORE: string[] = [
  'Round', 'Oval', 'Pear', 'Marquise', 'Emerald', 'Cushion', 'Princess', 'Radiant', 'Heart', 'Asscher',
]

/** Rarer trade shapes (VDB taxonomy) — surface under "See more" as feeds carry them. */
export const SHAPES_EXTENDED: string[] = [
  'Baguette', 'Trillion', 'Half Moon', 'Trapezoid', 'Kite', 'Shield', 'Bullet', 'Hexagon',
  'Octagon', 'Pentagon', 'Lozenge', 'Briolette', 'Rose', 'Old Mine', 'Old European', 'Carre',
  'Portuguese', 'Other',
]

/** Every canonical label (core + extended). 'Other' is the explicit catch-all. */
export const CANONICAL_SHAPES: string[] = [...SHAPES_CORE, ...SHAPES_EXTENDED]

const canonicalSet = new Set(CANONICAL_SHAPES.map((s) => s.toLowerCase()))

/** True when `v` is exactly one of the canonical labels (case-insensitive). */
export function isCanonicalShape(v: string | null | undefined): boolean {
  return !!v && canonicalSet.has(v.trim().toLowerCase())
}

/** Return the canonical-cased label for a case-insensitive match, else null. */
export function toCanonicalShape(v: string | null | undefined): string | null {
  if (!v) return null
  const t = v.trim().toLowerCase()
  return CANONICAL_SHAPES.find((s) => s.toLowerCase() === t) ?? null
}
